import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';

import { FlyoutType } from '@app/models/fly-out.model';
import { DriverService } from '@app/services/driver.service';
import { FlyoutService } from '@app/services/flyout.service';
import { PermissionService } from '@app/services/permission.service';
import { DialogNewInterventionComponent } from '@app/shared/components/dialog-new-intervention/dialog-new-intervention.component';
import { toTitleCase } from '@app/shared/helpers/string.helper';
import { NewFileModalComponent } from './tab-sections/new-file-modal/new-file-modal.component';
import { ToasterService } from '@app/services/toaster.service';
import { ExpandViewService } from '@app/services/expand-view.service';
import { MediaService } from '@app/services/media.service';
import { TabSectionsFilesComponent } from '@app/drivers/dialog-driver/tab-sections/tab-sections-files/tab-sections-files.component';
import { DialogNewHosComponent } from '@app/shared/components/dialog-new-hos/dialog-new-hos.component';

enum DriverTabs {
  DriverProfile = 'DriverProfile',
  DriverFiles = 'DriverFiles',
  DriverActivity = 'DriverActivity',
  DriverInterventions = 'DriverInterventions',
  DriverEvents = 'DriverEvents',
  DriverNotes = 'DriverNotes',
}

@Component({
  selector: 'app-dialog-driver',
  templateUrl: './dialog-driver.component.html',
  styleUrls: ['./dialog-driver.component.scss'],
})
export class DialogDriverComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedTabIndex: number = 0;
  selectedTabName: string = '';
  rowData!: any;
  showSmaller: boolean = false;
  showMenu: boolean = true;
  visibleTabs: string[] = [];

  cssContent: string = '';
  isLoadingPrintWeb: boolean = false;
  mobileWidth: number = 849;

  private tabItemPermissions: { [key: string]: () => boolean };
  private printIframe!: HTMLIFrameElement | null;
  private totalComponentsToExpand = 8;

  toTitleCase = toTitleCase;
  driverTabs = DriverTabs;

  data: any;
  isDialogMode = false;

  private expandViewCounterSubscription!: Subscription;
  private queryParamsSubscription!: Subscription;

  @ViewChild('tabGroup') tabGroup!: MatTabGroup;
  @ViewChild('modalContainer', { static: false }) modalContainer!: ElementRef;
  @ViewChild(TabSectionsFilesComponent)
  tabSectionsFilesComponent!: TabSectionsFilesComponent;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public injectedData: any,
    @Optional() private dialogRef: MatDialogRef<DialogDriverComponent>,
    @Optional() private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private permissionService: PermissionService,
    private flyoutService: FlyoutService,
    private driverService: DriverService,
    private toasterService: ToasterService,
    private http: HttpClient,
    private expandViewService: ExpandViewService,
    private location: Location,
    private mediaService: MediaService
  ) {
    this.isDialogMode = !!this.injectedData;
    if (this.isDialogMode) {
      this.data = this.injectedData;
    }
    this.rowData = this.data;
    if (this.data?.fromEvent) {
      this.showSmaller = true;
    } else {
      this.showSmaller = false;
    }
    if (this.rowData?.tab !== undefined) {
      this.selectedTabIndex = this.rowData.tab;
    }
    this.tabItemPermissions = {
      DriverProfile: () => this.canViewDriverProfile(),
      DriverFiles: () => this.canViewDriverFiles(),
      DriverActivity: () => this.canViewDriverActivity(),
      DriverInterventions: () => this.canViewDriverInterventions(),
      DriverEvents: () => this.canViewDriverEvents(),
      DriverNotes: () => this.canViewDriverNotes(),
    };
  }

  ngOnInit(): void {
    if (!this.isDialogMode) {
      this.route.queryParams.subscribe((params) => {
        this.data = {
          ...this.data,
          id: params['driverId'],
          terminal: params['terminal'],
          driverId: params['idDriver'],
        };
        if (this.data.id) {
          this.fetchDriverProfile();
        }
      });
    } else {
      this.fetchDriverProfile();
    }

    this.subscriptionToDialogState();
    this.updateVisibleTabs();
    this.subscriptionToURLParams();
    this.loadPrintStyles();
    this.updateSelectedTabState();

    this.expandViewCounterSubscription =
      this.expandViewService.expandViewCounter$.subscribe((count: number) => {
        if (count === this.totalComponentsToExpand) {
          this.printCurrentTabContent();
          this.expandViewService.resetExpandView();
        }
      });
  }

  ngAfterViewInit(): void {
    if (!this.isDialogMode) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }

  subscriptionToURLParams(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe((params) => {
      const tabName = params['tab'];
      if (tabName) {
        const index = this.visibleTabs.indexOf(tabName);
        if (index !== -1) {
          this.selectedTabIndex = index;
          this.onTabChange({ index: index });
        }
      }
    });
  }
  private updateSelectedTabState(): void {
    const tabsOrder = [
      DriverTabs.DriverProfile,
      DriverTabs.DriverFiles,
      DriverTabs.DriverActivity,
      DriverTabs.DriverInterventions,
      DriverTabs.DriverEvents,
      DriverTabs.DriverNotes,
    ];
    const visibleTabs = tabsOrder.filter((tab) => this.shouldShowTabItem(tab));
    this.selectedTabName = visibleTabs[this.selectedTabIndex];
  }

  ngOnDestroy(): void {
    if (this.expandViewCounterSubscription) {
      this.expandViewCounterSubscription.unsubscribe();
    }

    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }

    if (this.isDialogMode) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          tab: null,
          driverId: null,
          activity: null,
          eventId: null,
          idDriver: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  loadPrintStyles(): void {
    this.http
      .get('assets/css/print-screen-drivers.css', { responseType: 'text' })
      .subscribe({
        next: (css) => {
          this.cssContent = css;
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  fetchDriverProfile(): void {
    this.driverService.getDriverProfile(this.data.id).subscribe({
      next: (res) => {
        this.data = { ...this.data, ...res[0] };

        if (!this.rowData) {
          this.rowData = this.data;
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  subscriptionToDialogState(): void {
    this.checkIfTopDialog();
    this.dialog.afterOpened.subscribe((dialogRef) => {
      this.checkIfTopDialog();
      dialogRef.afterClosed().subscribe(() => {
        setTimeout(() => {
          this.checkIfTopDialog();
        });
      });
    });
  }

  checkIfTopDialog(): void {
    this.showMenu = this.flyoutService.isDialogOnTop(FlyoutType.DRIVER);
  }

  getImageUrl(driverId: string): string {
    return this.mediaService.getDriverImageURL(driverId);
  }

  onCloseClick(): void {
    this.dialogRef.close();
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    this.updateSelectedTabState();

    const selectedTabName = this.visibleTabs[this.selectedTabIndex];
    const currentUrl = this.location.path();

    const urlSearchParams = new URLSearchParams(currentUrl.split('?')[1] || '');

    urlSearchParams.set('driverId', this.data.id);
    if (this.data.driverId) {
      urlSearchParams.set('idDriver', this.data.driverId);
    }
    urlSearchParams.set('tab', selectedTabName);

    this.location.replaceState(
      `${currentUrl.split('?')[0]}?${urlSearchParams.toString()}`
    );

    switch (this.selectedTabName) {
      case DriverTabs.DriverActivity:
        this.totalComponentsToExpand = 8;
        break;
      case DriverTabs.DriverFiles:
        this.totalComponentsToExpand = 5;
        break;
      default:
        this.totalComponentsToExpand = 1;
        break;
    }

    const container: Element | null = document.querySelector(
      '.dialog-driver .mat-mdc-dialog-container .mdc-dialog__surface'
    );
    container?.setAttribute('style', 'overflow: hidden;');
    setTimeout(() => container?.setAttribute('style', 'overflow: visible;'), 1000);
  }

  onNewInterventionClick(): void {
    const isMobile = window.innerWidth <= this.mobileWidth;

    let dialogData: any = {
      driver: {
        driverId: this.data.driverId,
        driverName: this.data.driverName,
        terminal: this.data.terminal,
        organizationId: this.data.organizationId,
      },
    };

    if (this.permissionService.hasPermission('WRITE', 'My Interventions')) {
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        dialogData.assignee = {
          userId: currentUser.userId,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
        };
      }
    }

    const config = isMobile
      ? {
          panelClass: 'full-screen-dialog',
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          autoFocus: false,
          data: dialogData,
        }
      : {
          width: '800px',
          panelClass: 'single',
          data: dialogData,
        };

    this.dialog.open(DialogNewInterventionComponent, config);
  }

  onNewFileClick(): void {
    const isMobile = window.innerWidth <= this.mobileWidth;

    const dialogData = {
      ...this.data,
      driverGuid: this.data.id,
      delete: true,
      hideUploadNewFile: false,
    };

    const config = isMobile
      ? {
          panelClass: 'full-screen-dialog',
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          autoFocus: false,
          data: dialogData,
        }
      : {
          width: '660px',
          panelClass: 'single',
          data: dialogData,
        };

    const dialogRef = this.dialog.open(NewFileModalComponent, config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toasterService.showSuccess('The File was uploaded successfully');

        if (this.tabSectionsFilesComponent) {
          this.tabSectionsFilesComponent.loadSections();
        }
      }
    });
  }

  getCurrentUser() {
    const userDetailsString = localStorage.getItem('userData');
    if (userDetailsString) {
      const userDetails = JSON.parse(userDetailsString);
      const { objectId: userId, firstName, lastName } = userDetails.userInfo;
      return { userId, firstName, lastName };
    } else {
      console.error('User details not found in local storage');
      return null;
    }
  }

  goToInfoTab(): void {
    this.tabGroup.selectedIndex = 0;
    this.selectedTabIndex = 0;
  }

  goToTerminal(terminal: string): void {
    this.dialog.openDialogs.forEach((dialogRef) => dialogRef.close());
    this.router.navigate(['/drivers', terminal]);
  }

  shouldShowTabItem(tabItemName: string): boolean {
    const checkFunction = this.tabItemPermissions[tabItemName];
    return checkFunction ? checkFunction() : false;
  }

  canCreateInterventions(): boolean {
    return (
      this.permissionService.hasPermission('WRITE', 'All Interventions') ||
      this.permissionService.hasPermission('WRITE', 'My Interventions')
    );
  }

  canCreateFile(): boolean {
    return (
      this.permissionService.hasPermission('WRITE', 'Qualification Files') ||
      this.permissionService.hasPermission('WRITE', 'Qualification Files')
    );
  }

  canViewDriverProfile(): boolean {
    return this.permissionService.hasPermission('READ', 'Driver Profile');
  }

  canViewDriverFiles(): boolean {
    return (
      this.permissionService.hasPermission('WRITE', 'Qualification Files') ||
      this.permissionService.hasPermission('READ', 'Qualification Files')
    );
  }

  canViewDriverActivity(): boolean {
    return this.permissionService.hasPermission('READ', 'Driver Activity');
  }

  canViewDriverInterventions(): boolean {
    return (
      this.permissionService.hasPermission('WRITE', 'All Interventions') ||
      this.permissionService.hasPermission('READ', 'All Interventions')
    );
  }

  canViewDriverEvents(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'Events') ||
      this.permissionService.hasPermission('WRITE', 'Events')
    );
  }

  canViewDriverNotes(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'Driver Notes') ||
      this.permissionService.hasPermission('WRITE', 'Driver Notes')
    );
  }

  updateVisibleTabs(): void {
    this.visibleTabs = Object.keys(this.tabItemPermissions).filter((tab) =>
      this.tabItemPermissions[tab]()
    );
  }

  getTabIcon(tab: string): string {
    const icons: { [key: string]: string } = {
      DriverProfile: 'fa-th-list',
      DriverFiles: 'fa-folder-open',
      DriverActivity: 'fa-chart-line',
      DriverInterventions: 'fa-book',
      DriverEvents: 'fa-exclamation-triangle',
      DriverNotes: 'fa-sticky-note',
    };
    return icons[tab] || 'fa-question-circle';
  }

  isActiveTab(tabName: string): boolean {
    const tabsOrder = [
      DriverTabs.DriverProfile,
      DriverTabs.DriverFiles,
      DriverTabs.DriverActivity,
      DriverTabs.DriverInterventions,
      DriverTabs.DriverEvents,
      DriverTabs.DriverNotes,
    ];

    const visibleTabs = tabsOrder.filter((tab) => this.shouldShowTabItem(tab));
    return tabName === visibleTabs[this.selectedTabIndex];
  }

  onNewHOSClick(): void {
    const isMobile = window.innerWidth <= this.mobileWidth;

    const dialogData = {
      driverGuid: this.data.id,
      driver: {
        driverId: this.data.driverId,
        driverName: this.data.driverName,
        terminal: this.data.terminal,
        organizationId: this.data.organizationId,
      },
    };

    const config = isMobile
      ? {
          panelClass: 'full-screen-dialog',
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          autoFocus: false,
          data: dialogData,
        }
      : {
          maxWidth: '660px',
          panelClass: 'single',
          width: '100%',
          data: dialogData,
          autoFocus: false,
        };

    this.dialog.open(DialogNewHosComponent, config);
  }

  canCreateHOS(): boolean {
    return this.permissionService.hasPermission('WRITE', 'Manual HOS Violation');
  }

  isSafari(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('safari') && !ua.includes('chrome');
  }

  onPrintClick(): void {
    this.isLoadingPrintWeb = true;
    this.expandViewService.triggerExpandView();
  }

  printCurrentTabContent(): void {
    this.isLoadingPrintWeb = true;
    if (this.isSafari()) {
      setTimeout(() => {
        if (this.modalContainer) {
          const modalHTML = this.generateModalHTML();

          // Reuse the iframe if it has already been created
          if (!this.printIframe) {
            this.printIframe = document.createElement('iframe');
            this.printIframe.style.position = 'absolute';
            this.printIframe.style.width = '0px';
            this.printIframe.style.height = '0px';
            this.printIframe.style.border = 'none';
            document.body.appendChild(this.printIframe);
          }

          const iframeDoc =
            this.printIframe.contentWindow?.document || this.printIframe.contentDocument;
          if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(modalHTML);
            iframeDoc.close();

            // Wait for the iframe to load before executing print
            this.printIframe.onload = () => {
              this.printIframe?.contentWindow?.focus();
              this.printIframe?.contentWindow?.print();
            };
          }
        }
        this.isLoadingPrintWeb = false;
      }, 1000);
    } else {
      setTimeout(() => {
        if (this.modalContainer) {
          const modalHTML = this.generateModalHTML();

          const blob = new Blob([modalHTML], { type: 'text/html' });
          const url = URL.createObjectURL(blob);

          const printWindow = window.open(url, '_blank');

          if (printWindow) {
            printWindow.onload = () => {};
          }
        }
        this.isLoadingPrintWeb = false;
      }, 1000);
    }
  }

  generateModalHTML(): string {
    const eventTitle = `${this.data?.driverId} - Driver`;

    const currentUser = this.getCurrentUser();
    const userName = currentUser
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : 'Unknown User';

    const containerClone = this.modalContainer.nativeElement.cloneNode(
      true
    ) as HTMLElement;

    const layoutMobileElements = containerClone.querySelectorAll('.layout-mobile');
    layoutMobileElements.forEach((el) => el.remove());

    let finalHTML = containerClone.innerHTML;

    finalHTML = finalHTML.replaceAll(
      '/assets/images/fallback.png',
      window.location.origin + '/assets/images/fallback.png'
    );

    return `
      <html>
        <head>
          <title>${eventTitle} (Generated for ${userName})</title>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" rel="stylesheet">
          <style>${this.cssContent}</style>
        </head>
        <body>
          <div class="button-container">
            <button onclick="window.close()">Go Back</button>
            <button onclick="window.print()">Print</button>
          </div>
          <div class="printable-modal">
            ${finalHTML}
          </div>
        </body>
      </html>
    `;
  }

  onClosePage(): void {
    this.router.navigate(['/drivers']);
  }
}
